// Pure helpers behind the Hooks page's all-hooks list: the flat rows
// (hook joined to its course), the status/search/category filter with
// its "touched rows stay" rule, the default order, and the column set.
// Kept free of React so they can be tested directly.

export const STATUS_FILTERS = {
    APPROVED: 'approved',
    DRAFT: 'draft',
    ALL: 'all',
};

export const STATUS_FILTER_OPTIONS = [
    { label: 'Approved', value: STATUS_FILTERS.APPROVED },
    { label: 'Draft', value: STATUS_FILTERS.DRAFT },
    { label: 'All statuses', value: STATUS_FILTERS.ALL },
];

export const DEFAULT_STATUS_FILTER = STATUS_FILTERS.APPROVED;

// Every column the list can show; `alwaysVisible` ones cannot be hidden.
// Explanation and the dates are off by default: they make rows tall.
export const ALL_COLUMNS = [
    { key: 'id', label: 'ID', alwaysVisible: true },
    { key: 'course', label: 'Course', alwaysVisible: true },
    { key: 'question', label: 'Question', alwaysVisible: true },
    { key: 'options', label: 'Options' },
    { key: 'explanation', label: 'Explanation' },
    { key: 'status', label: 'Status' },
    { key: 'approved', label: 'Approved' },
    { key: 'updated', label: 'Updated' },
];

export const DEFAULT_VISIBLE_COLUMNS = ['id', 'course', 'question', 'options', 'status'];

// One row per hook with what the table needs from its course (status,
// categories) and precomputed sort keys. `course_name` comes with the
// hook already; the course list fills in the rest.
export function buildHookRows(hooks, courses = []) {
    const byId = Object.fromEntries(courses.map(c => [c.id, c]));
    return hooks.map(hook => {
        const course = byId[hook.course_id] || {};
        return {
            ...hook,
            course_name: hook.course_name || course.name || '',
            course_status: course.status || '',
            categoryIds: course.categories || [],
            answer: hook.options[hook.correct_index] || '',
            course_sort: (hook.course_name || course.name || '').toLowerCase(),
            status_sort: hook.status === 'approved' ? 1 : 0,
        };
    });
}

const byCourseThenPosition = (a, b) => a.course_sort.localeCompare(b.course_sort)
    || (a.position - b.position) || (a.id - b.id);

// Rows matching the status filter, the text `query` (question or course
// name) and, when set, `categoryId`, in course-then-position order.
// `touchedIds` are rows whose status changed in this session: they stay
// visible whatever the status filter says, so a revoke or an edit does
// not make the row vanish under the editor's cursor.
export function filterHookRows(rows, {
    status = DEFAULT_STATUS_FILTER, query = '', categoryId = null, touchedIds = new Set(),
}) {
    const q = query.trim().toLowerCase();
    const matches = (row) => {
        if (q && !row.question.toLowerCase().includes(q) && !row.course_sort.includes(q)) return false;
        if (categoryId != null && !row.categoryIds.includes(categoryId)) return false;
        if (touchedIds.has(row.id)) return true;
        if (status === STATUS_FILTERS.ALL) return true;
        return row.status === status;
    };
    return rows.filter(matches).sort(byCourseThenPosition);
}
